/* eslint-env es2020 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import ProtonWebSDK from "@proton/web-sdk";

import APP_LOGO from "./assets/nch_logo_512.png";

function getEnv(key, fallback = "") {
  try {
    // eslint-disable-next-line no-undef
    const env = typeof process !== "undefined" && process?.env ? process.env : {};
    const v = String(env[key] || "").trim();
    return v || fallback;
  } catch {
    return fallback;
  }
}

const CONTRACT = getEnv("REACT_APP_CONTRACT_ACCOUNT", "nftrewards");
const APP_NAME = getEnv("REACT_APP_APP_NAME", "ImaginatioNFT Rewards Viewer");

const DEFAULT_RPC = (() => {
  const v = getEnv("REACT_APP_RPC_ENDPOINT", getEnv("REACT_APP_RPC", ""));
  return v || "https://xpr-mainnet-api.bloxprod.io";
})();

const FALLBACK_RPCS = [
  "https://api.protonnz.com",
  "https://proton-public.neftyblocks.com",
  "https://proton.cryptolions.io",
];

const DEFAULT_TOKEN_SUPPLY = (() => {
  const raw = getEnv("REACT_APP_TOTAL_REWARDS", "2500000");
  const cleaned = String(raw || "").replace(/[,_\s]/g, "");
  const n = Number(cleaned);
  return Number.isFinite(n) && n > 0 ? n : 2_500_000;
})();
const POOL_WEIGHTS = [
  { id: "pool1", label: "Pool 1", weight: 1.0 },
  { id: "pool2", label: "Pool 2", weight: 0.6 },
  { id: "pool3", label: "Pool 3", weight: 0.7 },
  { id: "pool4", label: "Pool 4", weight: 1.6 },
];

const ENV_ATOMIC_BASE = getEnv("REACT_APP_ATOMIC_API_BASE", "");
function normalizeAtomicBase(raw) {
  const v = String(raw || "").trim();
  if (!v) return "";
  if (v.includes("/atomicassets/v1")) return v.replace(/\/+$/, "");
  return `${v.replace(/\/+$/, "")}/atomicassets/v1`;
}

const ATOMIC_API_BASES = [
  normalizeAtomicBase(ENV_ATOMIC_BASE),
  "https://xpr-mainnet-atm-api.bloxprod.io/atomicassets/v1",
  "https://proton.api.atomicassets.io/atomicassets/v1",
  "https://aa-xprnetwork-main.saltant.io/atomicassets/v1",
  "https://proton.pink.gg/atomicassets/v1",
].filter(Boolean);

function uniq(arr) {
  const out = [];
  const seen = new Set();
  for (const x of arr || []) {
    const k = String(x || "");
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(k);
  }
  return out;
}

function normalizeRpcEndpoint(v) {
  return String(v || "").trim().replace(/\/+$/, "");
}

function serializeError(e) {
  if (!e) return { message: "Unknown error" };
  return { message: e.message ?? String(e), name: e.name, stack: e.stack };
}

async function postJson(url, body) {
  const res = await fetch(url, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body ?? {}),
  });
  const txt = await res.text();
  try {
    return JSON.parse(txt);
  } catch {
    return null;
  }
}

async function readTableRows(rpc, { code, scope, table, limit = 100, lower_bound, reverse = false, index_position, key_type }) {
  const rpcBase = normalizeRpcEndpoint(rpc);
  const body = {
    json: true,
    code,
    scope,
    table,
    limit,
    reverse: !!reverse,
    show_payer: false,
  };
  if (lower_bound !== undefined && lower_bound !== null && String(lower_bound).trim() !== "") body.lower_bound = lower_bound;
  if (index_position) body.index_position = index_position;
  if (key_type) body.key_type = key_type;
  return await postJson(`${rpcBase}/v1/chain/get_table_rows`, body);
}

async function fetchAllRows(rpc, params) {
  const out = [];
  let lower = params.lower_bound;
  for (let i = 0; i < 10000; i++) {
    const resp = await readTableRows(rpc, { ...params, lower_bound: lower, limit: params.limit ?? 1000 });
    const rows = resp?.rows || [];
    out.push(...rows);
    if (!resp?.more) break;
    const next = resp?.next_key;
    if (next === undefined || next === null || String(next) === "") break;
    lower = next;
  }
  return out;
}

function derivePoolScope(poolRow) {
  const h = poolRow?.poolHash ?? poolRow?.poolhash ?? poolRow?.pool_hash;
  if (h === undefined || h === null) return "";
  return String(h);
}

function parseImmutableRequirements(reqArr) {
  const arr = Array.isArray(reqArr) ? reqArr : [];
  const out = [];
  for (const s of arr) {
    const t = String(s || "").trim();
    if (!t) continue;
    const idx = t.indexOf("=");
    if (idx <= 0 || idx >= t.length - 1) continue;
    const key = t.slice(0, idx).trim();
    const value = t.slice(idx + 1).trim();
    if (!key || !value) continue;
    out.push({ key, value });
  }
  return out;
}

function mergeImmutableData(asset) {
  const out = { ...(asset?.template?.immutable_data || {}), ...(asset?.immutable_data || {}) };
  return out;
}

function matchesRequirements(asset, reqPairs) {
  if (!reqPairs || reqPairs.length === 0) return true;
  const data = mergeImmutableData(asset);
  for (const { key, value } of reqPairs) {
    if (!Object.prototype.hasOwnProperty.call(data, key)) return false;
    if (String(data[key]) !== String(value)) return false;
  }
  return true;
}

async function fetchAtomicJson(path, params) {
  const p = params || {};
  let lastErr = null;
  const attempted = [];

  for (const base of ATOMIC_API_BASES) {
    attempted.push(base);
    try {
      const u = new URL(`${base}${path}`);
      Object.keys(p).forEach((k) => {
        const v = p[k];
        if (v === undefined || v === null || String(v) === "") return;
        u.searchParams.set(k, String(v));
      });

      const controller = new AbortController();
      const to = setTimeout(() => controller.abort(), 20000);
      let res;
      try {
        res = await fetch(u.toString(), { signal: controller.signal });
      } finally {
        clearTimeout(to);
      }

      if (!res.ok) {
        const status = res.status;
        const e = new Error(`Atomic API error (${status})`);
        // @ts-ignore
        e.__atomicStatus = status;
        throw e;
      }
      const json = await res.json();
      if (json && json.success === false) throw new Error(json.message || "Atomic API error");
      return json;
    } catch (e) {
      lastErr = e;
      const msg = String(e?.message || e || "");
      const status = e?.__atomicStatus;
      const retryable =
        msg.includes("NetworkError") ||
        msg.includes("Failed to fetch") ||
        msg.includes("aborted") ||
        msg.includes("AbortError") ||
        status === 429 ||
        status === 502 ||
        status === 503 ||
        status === 504;
      if (!retryable) break;
    }
  }

  const lastMsg = String(lastErr?.message || lastErr || "").trim();
  const msg = `AtomicAssets API request failed. Tried: ${attempted.join(", ")}. ${lastMsg ? `Last error: ${lastMsg}` : ""}`;
  throw new Error(msg);
}

async function fetchAllAtomicAssets({ collectionName, owner, limit = 1000, maxPages = 200 }) {
  const out = [];
  for (let page = 1; page <= maxPages; page++) {
    const json = await fetchAtomicJson("/assets", {
      collection_name: collectionName,
      owner,
      page,
      limit,
      order: "desc",
      sort: "asset_id",
      burned: "false",
    });
    const rows = json?.data || [];
    out.push(...rows);
    if (!rows || rows.length < limit) break;
  }
  return out;
}

async function mapLimit(items, limit, fn) {
  const arr = Array.isArray(items) ? items : [];
  const out = new Array(arr.length);
  let idx = 0;
  const workers = new Array(Math.max(1, limit)).fill(0).map(async () => {
    while (idx < arr.length) {
      const cur = idx++;
      out[cur] = await fn(arr[cur], cur);
    }
  });
  await Promise.all(workers);
  return out;
}

function parseAssetString(assetStr) {
  const s = String(assetStr || "").trim();
  if (!s) return null;
  const parts = s.split(/\s+/);
  if (parts.length < 2) return null;
  const qty = parts[0];
  const symbol = parts[1];

  const m = qty.match(/^(-?\d+)(?:\.(\d+))?$/);
  if (!m) return null;
  const neg = m[1].startsWith("-");
  const intPart = m[1].replace("-", "");
  const fracPart = m[2] || "";
  const precision = fracPart.length;
  const joined = `${intPart}${fracPart}`.replace(/^0+(\d)/, "$1");
  const amountInt = BigInt(joined || "0") * (neg ? -1n : 1n);
  return { amountInt, precision, symbol };
}

function bigIntToFloat(amountInt, precision) {
  const n = typeof amountInt === "bigint" ? amountInt : BigInt(amountInt || 0);
  const p = Number(precision || 0);
  const sign = n < 0n ? -1 : 1;
  const abs = n < 0n ? -n : n;
  const pow = 10n ** BigInt(p);
  const whole = abs / pow;
  const frac = abs % pow;
  const fracStr = p ? String(frac).padStart(p, "0") : "";
  const asStr = p ? `${whole}.${fracStr}` : String(whole);
  const f = Number(asStr);
  return sign * (Number.isFinite(f) ? f : 0);
}

function parseNumberInput(value, fallback = 0) {
  const raw = String(value ?? "").trim();
  if (!raw) return fallback;

  // Accept common formats:
  // - en-US: 2,500,000.12 (commas thousands, dot decimal)
  // - en-US no decimals: 2,500,000
  // - de-DE style decimals: 2500000,12 (comma decimal)
  // - de-DE thousands dots: 2.500.000
  let s = raw.replace(/\s/g, "").replace(/_/g, "");

  const commaCount = (s.match(/,/g) || []).length;
  const dotCount = (s.match(/\./g) || []).length;

  if (commaCount > 0 && dotCount > 0) {
    // If both are present, treat commas as thousands separators.
    s = s.replace(/,/g, "");
  } else if (commaCount > 0 && dotCount === 0) {
    if (commaCount > 1) {
      // e.g. 2,500,000
      s = s.replace(/,/g, "");
    } else {
      // Single comma: could be thousands (2,500) or decimal (2,5)
      const parts = s.split(",");
      const left = parts[0] || "";
      const right = parts[1] || "";
      const looksLikeThousands = /^-?\d{1,3}$/.test(left) && right.length === 3;
      s = looksLikeThousands ? `${left}${right}` : `${left}.${right}`;
    }
  } else if (commaCount === 0 && dotCount > 1) {
    // e.g. 2.500.000
    s = s.replace(/\./g, "");
  }

  const n = Number(s);
  return Number.isFinite(n) ? n : fallback;
}

function truncToDecimals(value, decimals) {
  const v = Number(value);
  const d = Math.max(0, Number(decimals || 0));
  if (!Number.isFinite(v)) return NaN;
  const pow = 10 ** d;
  // Truncate (not round) and preserve sign
  return (v < 0 ? -1 : 1) * Math.floor(Math.abs(v) * pow) / pow;
}

function formatFixedTrunc(n, decimals = 4) {
  const v = truncToDecimals(n, decimals);
  if (!Number.isFinite(v)) return "—";
  // toFixed would normally round, but we already truncated above.
  return v.toFixed(decimals);
}

function formatIntPlain(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return String(Math.trunc(v));
}


function StatBox({ label, value, sub, className = "" }) {
  return (
    <div className={`rounded-2xl border border-slate-200 bg-white p-4 shadow-sm ${className}`.trim()}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-extrabold text-slate-900">{value}</div>
      {sub ? <div className="mt-1 text-xs text-slate-500">{sub}</div> : null}
    </div>
  );
}

function PoolCard({
  slotLabel,
  pools,
  selectedPoolHash,
  onPoolChange,
  factorValue,
  onFactorChange,
  pool,
  calcEntry,
  eligible,
  rewardValue,
  rewardSub,
  isCalculated,
  showReward,
}) {
  const poolName = pool ? String(pool.poolKey || slotLabel) : slotLabel;
  const status = calcEntry?.loading ? "Calculating…" : calcEntry?.error ? "Error" : isCalculated ? "Calculated" : "";
  const activeCard = !!selectedPoolHash && isCalculated;
  const cardClass = activeCard ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-white";

  return (
    <div className={`rounded-2xl border p-4 shadow-sm ${cardClass}`}> 
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-slate-900">{poolName}</div>
          {pool ? <div className="mt-0.5 text-xs text-slate-500">{String(pool.collectionName || "")}</div> : null}
        </div>
        {status ? (
          <div
            className={`rounded-full px-2 py-1 text-[11px] font-semibold ${
              calcEntry?.error
                ? "border border-red-200 bg-red-50 text-red-800"
                : calcEntry?.loading
                  ? "border border-slate-200 bg-slate-50 text-slate-700"
                  : "border border-emerald-200 bg-emerald-50 text-emerald-800"
            }`}
          >
            {status}
          </div>
        ) : null}
      </div>

      <div className="mt-3 flex flex-col gap-1">
        <div className="flex items-end justify-between gap-3">
          <div className="text-xs text-slate-500">Select pool</div>
          <div className="flex items-center gap-2">
            <div className="text-[10px] uppercase tracking-wide text-slate-400">Factor</div>
            <input
              className="w-24 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
              value={factorValue}
              onChange={(e) => onFactorChange?.(e.target.value)}
              inputMode="decimal"
              placeholder="1.0"
            />
          </div>
        </div>
        <select
          className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
          value={selectedPoolHash}
          onChange={(e) => onPoolChange(e.target.value)}
        >
          <option value="">—</option>
          {(pools || []).map((p) => (
            <option key={String(p.poolHash)} value={String(p.poolHash)}>
              {String(p.poolKey || "pool")} • {String(p.collectionName || "")}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3">
        <StatBox label="Eligible NFTs" value={formatIntPlain(eligible)} sub={pool ? String(pool.poolKey || "") : "—"} />
        <StatBox
          label="Pool Reward"
          value={showReward ? rewardValue : isCalculated ? "" : "—"}
          sub={showReward ? rewardSub : ""}
          className={isCalculated ? "!border-emerald-200 !bg-emerald-50" : ""}
        />
      </div>
    </div>
  );
}


export default function ReadOnlyRewardsDashboard() {
  const [rpc] = useState(DEFAULT_RPC);

  const [link, setLink] = useState(null);
  const [session, setSession] = useState(null);
  const [auth, setAuth] = useState(null);

  const actor = auth?.actor ? String(auth.actor) : "";
  const isContract = actor && actor.toLowerCase() === String(CONTRACT).toLowerCase();

  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [pools, setPools] = useState([]);
  const [reqNfts, setReqNfts] = useState([]);

  const allowedProjects = useMemo(() => {
    if (isContract) return projects;
    const a = String(actor || "").trim();
    if (!a) return [];
    return (projects || []).filter((p) => (p.allowedAccounts || []).map((x) => String(x)).includes(a));
  }, [projects, actor, isContract]);

  const [selectedProjectId, setSelectedProjectId] = useState("");
  const selectedProject = useMemo(() => {
    const id = String(selectedProjectId || "");
    return (allowedProjects || []).find((p) => String(p.projectId) === id) || null;
  }, [allowedProjects, selectedProjectId]);

  const poolsForProject = useMemo(() => {
    const pid = String(selectedProject?.projectId || "");
    if (!pid) return [];
    const arr = (pools || []).filter((p) => String(p.projectId ?? p.project_id ?? "") === pid);
    arr.sort((a, b) => String(a.poolKey || "").localeCompare(String(b.poolKey || "")));
    return arr;
  }, [pools, selectedProject]);

  // Claim calculator results: by poolHash
  const [calcByPool, setCalcByPool] = useState({});
  const atomicCacheRef = useRef(new Map());
  const excludedAssetsCacheRef = useRef(new Map());

  // Reward calculator pool selections
  const [rewardPoolHashBySlot, setRewardPoolHashBySlot] = useState(() => ({ pool1: "", pool2: "", pool3: "", pool4: "" }));

  // Total rewards (token supply) used for the per-claim reward calculation (client-side only)
  // Show without thousands separators by default; user can still type 2,500,000 etc.
  const [totalRewardsInput, setTotalRewardsInput] = useState(String(DEFAULT_TOKEN_SUPPLY));
  const [factorInputs, setFactorInputs] = useState(() => ({
    pool1: "1.0",
    pool2: "0.6",
    pool3: "0.7",
    pool4: "1.6",
  }));
  const factorNumbers = useMemo(() => {
    const out = {};
    for (const slot of POOL_WEIGHTS) {
      out[slot.id] = parseNumberInput(factorInputs?.[slot.id], slot.weight);
    }
    return out;
  }, [factorInputs]);

    const totalRewards = useMemo(() => {
    const n = parseNumberInput(totalRewardsInput, 0);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [totalRewardsInput]);

  useEffect(() => {
    // pick first four pools when project OR pool list changes
    const arr = poolsForProject;
    setRewardPoolHashBySlot((prev) => {
      const next = { ...prev };
      next.pool1 = arr[0] ? String(arr[0].poolHash) : "";
      next.pool2 = arr[1] ? String(arr[1].poolHash) : "";
      next.pool3 = arr[2] ? String(arr[2].poolHash) : "";
      next.pool4 = arr[3] ? String(arr[3].poolHash) : "";
      return next;
    });
  }, [selectedProjectId, poolsForProject]);

  // Restore session on load
  useEffect(() => {
    let cancelled = false;
    async function tryRestore() {
      try {
        if (session || auth) return;
        const rpcNormalized = normalizeRpcEndpoint(rpc);
        const endpoints = uniq([rpcNormalized, ...FALLBACK_RPCS]);
        const { link: l, session: s } = await ProtonWebSDK({
          linkOptions: { endpoints, restoreSession: true },
          transportOptions: { requestAccount: CONTRACT, backButton: true },
          selectorOptions: {
            appName: APP_NAME,
            showSelector: false,
            enabledWalletTypes: ["webauth", "proton"],
            dialogRootNode: document.body,
          },
        });
        if (cancelled) return;
        if (s) {
          setLink(l);
          setSession(s);
          setAuth(s.auth);
        }
      } catch {
        // ignore
      }
    }
    const t = setTimeout(() => tryRestore(), 100);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function connect() {
    setError("");
    setNotice("");
    try {
      const rpcNormalized = normalizeRpcEndpoint(rpc);
      const endpoints = uniq([rpcNormalized, ...FALLBACK_RPCS]);
      const { link: l, session: s } = await ProtonWebSDK({
        linkOptions: { endpoints },
        transportOptions: { requestAccount: CONTRACT, backButton: true },
        selectorOptions: {
          appName: APP_NAME,
          showSelector: true,
          enabledWalletTypes: ["webauth", "proton"],
          dialogRootNode: document.body,
        },
      });
      if (s) {
        setLink(l);
        setSession(s);
        setAuth(s.auth);
      }
    } catch (e) {
      setError(serializeError(e).message);
    }
  }

  async function disconnect() {
    setError("");
    setNotice("");
    try {
      if (link && session) await link.removeSession(session);
    } catch {
      // ignore
    }
    setLink(null);
    setSession(null);
    setAuth(null);
    setProjects([]);
    setPools([]);
    setReqNfts([]);
    setSelectedProjectId("");
    setCalcByPool({});
    atomicCacheRef.current = new Map();
    excludedAssetsCacheRef.current = new Map();
  }

  async function loadCoreState({ silent = false } = {}) {
    if (!actor) return;
    if (!silent) {
      setError("");
      setNotice("");
    }
    setProjectsLoading(true);
    // Clear caches so a refresh reflects on-chain changes
    atomicCacheRef.current = new Map();
    excludedAssetsCacheRef.current = new Map();
    try {
      const allProjects = await fetchAllRows(rpc, { code: CONTRACT, scope: CONTRACT, table: "nprojects", limit: 1000 });
      setProjects(allProjects);

      const [poolRows, reqRows] = await Promise.all([
        fetchAllRows(rpc, { code: CONTRACT, scope: CONTRACT, table: "npools", limit: 1000 }),
        fetchAllRows(rpc, { code: CONTRACT, scope: CONTRACT, table: "nreqnfts", limit: 1000 }),
      ]);
      setPools(poolRows);
      setReqNfts(reqRows);

      // Select first allowed project if none
      const allowed = isContract
        ? allProjects
        : (allProjects || []).filter((p) => (p.allowedAccounts || []).map((x) => String(x)).includes(String(actor)));

      if (!selectedProjectId && allowed.length) {
        setSelectedProjectId(String(allowed[0].projectId));
      }

      if (!allowed.length && !isContract) {
        setNotice(`Your account (${actor}) is not listed in allowedAccounts for any project.`);
      }
    } catch (e) {
      setError(serializeError(e).message);
    } finally {
      setProjectsLoading(false);
    }
  }

  // Load core state after login
  useEffect(() => {
    if (!actor) return;
    loadCoreState({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actor]);

  async function getExcludedAssetSet(projectId) {
    const pid = String(projectId ?? "").trim();
    if (!pid) return new Set();

    const cache = excludedAssetsCacheRef.current;
    if (cache.has(pid)) return cache.get(pid);

    try {
      const rows = await fetchAllRows(rpc, { code: CONTRACT, scope: pid, table: "nexclassets", limit: 1000 });
      const set = new Set(
        (Array.isArray(rows) ? rows : [])
          .map((r) => String(r?.assetId ?? r?.asset_id ?? "").trim())
          .filter((x) => x && x !== "0"),
      );
      cache.set(pid, set);
      return set;
    } catch {
      const set = new Set();
      cache.set(pid, set);
      return set;
    }
  }

  async function computeClaimCalcForPool(poolRow) {
    const poolHash = String(poolRow?.poolHash || "");
    if (!poolHash) return;

    setCalcByPool((prev) => ({
      ...prev,
      [poolHash]: { ...(prev?.[poolHash] || {}), loading: true, error: "" },
    }));

    try {
      const collectionName = String(poolRow?.collectionName || "").trim();
      if (!collectionName) throw new Error("Pool is missing collectionName.");

      const poolReqPairs = parseImmutableRequirements(poolRow.requiredImmutable || []);

      const rulesForPool = (reqNfts || []).filter((r) => String(r.poolKey) === String(poolRow.poolKey));
      const activeRules = rulesForPool
        .filter((r) => !!r?.active)
        .map((r) => ({
          reqId: String(r.reqId || ""),
          collectionName: String(r.collectionName || "").trim(),
          nftcount: Number(r.nftcount || 1),
          reqPairs: parseImmutableRequirements(r.requiredImmutable || []),
        }))
        .filter((r) => r.collectionName && r.nftcount > 0);

      const collectionCache = atomicCacheRef.current;

      async function getCollectionAssets(coll) {
        const c = String(coll || "").trim();
        if (!c) return [];
        if (collectionCache.has(c)) return collectionCache.get(c);
        const assets = await fetchAllAtomicAssets({ collectionName: c, limit: 1000, maxPages: 200 });
        collectionCache.set(c, assets);
        return assets;
      }

      const allPoolAssets = await getCollectionAssets(collectionName);

      // Excluded claim assets (project-scoped table `nexclassets`)
      const projectId = poolRow?.projectId ?? poolRow?.project_id ?? 0;
      const excludedSet = await getExcludedAssetSet(projectId);

      const poolMatchedAssets = [];
      let matchedBeforeExclude = 0;
      let excludedMatchedCount = 0;

      for (const a of allPoolAssets) {
        if (!matchesRequirements(a, poolReqPairs)) continue;
        matchedBeforeExclude++;
        const id = String(a?.asset_id || a?.assetId || "").trim();
        if (!id) continue;
        if (excludedSet.size && excludedSet.has(id)) {
          excludedMatchedCount++;
          continue;
        }
        poolMatchedAssets.push(a);
      }

      // Claims (on-chain)
      const scope = derivePoolScope(poolRow);
      const claimRows = scope
        ? await fetchAllRows(rpc, { code: CONTRACT, scope, table: "nclaims", limit: 1000 })
        : [];

      const claimedSet = new Set((Array.isArray(claimRows) ? claimRows : []).map((r) => String(r?.assetId ?? r?.asset_id ?? "")));
      const claimedAccountsSet = new Set(
        (Array.isArray(claimRows) ? claimRows : []).map((r) => String(r?.account || "")).filter(Boolean)
      );

      // For each dependent rule, build an owner->count map by scanning the rule collection once.
      const ruleOwnerCounts = await mapLimit(activeRules, 2, async (rule) => {
        const assets = await getCollectionAssets(rule.collectionName);
        const matched = assets.filter((a) => matchesRequirements(a, rule.reqPairs));
        const counts = new Map();
        for (const a of matched) {
          const o = String(a?.owner || "");
          if (!o) continue;
          counts.set(o, (counts.get(o) || 0) + 1);
        }
        return { rule, counts };
      });

      function getAssetCollectionName(a) {
        return String(a?.collection_name || a?.collection?.collection_name || a?.collection?.collectionName || "");
      }

      function getAssetId(a) {
        return String(a?.asset_id || a?.assetId || "");
      }

      const eligibleAfterRules = activeRules.length === 0
        ? poolMatchedAssets
        : await (async () => {
            const list = poolMatchedAssets;
            const checks = await mapLimit(list, 10, async (asset) => {
              const owner = String(asset?.owner || "");
              if (!owner) return false;
              for (const item of ruleOwnerCounts) {
                const rule = item.rule;
                const baseCount = item.counts.get(owner) || 0;
                // emulate excludeAssetId behavior (best effort)
                let effCount = baseCount;
                if (rule.collectionName === getAssetCollectionName(asset) && matchesRequirements(asset, rule.reqPairs)) {
                  effCount = Math.max(0, effCount - 1);
                }
                if (effCount < rule.nftcount) return false;
              }
              return true;
            });
            return list.filter((_, i) => !!checks[i]);
          })();

      const eligibleIds = new Set(eligibleAfterRules.map(getAssetId));
      const claimedEligibleCount = Array.from(claimedSet).filter((id) => eligibleIds.has(String(id))).length;
      const remainingEligibleCount = Math.max(0, eligibleAfterRules.length - claimedEligibleCount);

      const rewardParsed = parseAssetString(poolRow.reward);
      const claimedSumInt = (Array.isArray(claimRows) ? claimRows : []).reduce((acc, r) => {
        const p = parseAssetString(r?.amount);
        if (!p || !rewardParsed) return acc;
        if (p.symbol !== rewardParsed.symbol || p.precision !== rewardParsed.precision) return acc;
        return acc + p.amountInt;
      }, 0n);

      const totalPotential = rewardParsed ? rewardParsed.amountInt * BigInt(eligibleAfterRules.length) : 0n;
      const remainingPotential = rewardParsed ? rewardParsed.amountInt * BigInt(remainingEligibleCount) : 0n;

      const data = {
        updatedAt: Date.now(),
        poolKey: String(poolRow.poolKey || ""),
        poolCollection: collectionName,
        poolAssetsTotal: allPoolAssets.length,
        poolMatchedBeforeExclude: matchedBeforeExclude,
        excludedAssetIdsCount: excludedSet.size,
        excludedMatchedCount: excludedMatchedCount,
        poolMatchedAfterExclude: poolMatchedAssets.length,
        eligibleNfts: eligibleAfterRules.length,
        claimedNfts: claimedSet.size,
        claimedAccounts: claimedAccountsSet.size,
        claimedEligibleNfts: claimedEligibleCount,
        remainingEligibleNfts: remainingEligibleCount,
        reward: rewardParsed,
        claimedSumInt,
        totalPotential,
        remainingPotential,
        activeRulesCount: activeRules.length,
      };

      setCalcByPool((prev) => ({ ...prev, [poolHash]: { loading: false, error: "", data } }));
    } catch (e) {
      setCalcByPool((prev) => ({
        ...prev,
        [poolHash]: { ...(prev?.[poolHash] || {}), loading: false, error: serializeError(e).message },
      }));
    }
  }

  const rewardInputs = useMemo(() => {
    const byHash = new Map((poolsForProject || []).map((p) => [String(p.poolHash), p]));
    const slots = {};
    for (const slot of POOL_WEIGHTS) {
      const h = String(rewardPoolHashBySlot?.[slot.id] || "");
      slots[slot.id] = h && byHash.has(h) ? byHash.get(h) : null;
    }
    return slots;
  }, [poolsForProject, rewardPoolHashBySlot]);

  const rewardNumbers = useMemo(() => {
    const N = {};
    for (const slot of POOL_WEIGHTS) {
      const pool = rewardInputs[slot.id];
      const h = pool ? String(pool.poolHash) : "";
      const n = h && calcByPool?.[h]?.data ? Number(calcByPool[h].data.eligibleNfts || 0) : 0;
      N[slot.id] = n;
    }

    const totalBonus = POOL_WEIGHTS.reduce(
      (acc, slot) => acc + (factorNumbers[slot.id] || 0) * (N[slot.id] || 0),
      0
    );
    const tokensPerBonusPoint = totalBonus > 0 && totalRewards > 0 ? totalRewards / totalBonus : 0;

    const rewards = {};
    for (const slot of POOL_WEIGHTS) {
      rewards[slot.id] = tokensPerBonusPoint * (factorNumbers[slot.id] || 0);
    }
    return { N, totalBonus, tokensPerBonusPoint, rewards };
  }, [rewardInputs, calcByPool, totalRewards, factorNumbers]);

  async function computeSelectedRewardPools() {
    const list = POOL_WEIGHTS.map((s) => rewardInputs[s.id]).filter(Boolean);
    // Sequential to avoid hammering RPC / Atomic endpoints
    for (const p of list) {
      // eslint-disable-next-line no-await-in-loop
      await computeClaimCalcForPool(p);
    }
  }

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <img src={APP_LOGO} alt="logo" className="h-10 w-10 rounded-xl" />
            <div>
              <div className="font-semibold text-slate-900">{APP_NAME}</div>
              <div className="text-xs text-slate-500">Shows only projects/pools your account is allowed to view. No write transactions.</div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {actor ? (
              <>
                <div className="text-sm text-slate-700">
                  <span className="text-slate-400">Account:</span> <span className="font-semibold">{actor}</span>
                </div>
                <button
                  type="button"
                  onClick={disconnect}
                  className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm hover:bg-slate-50"
                >
                  Disconnect
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={connect}
                className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
              >
                Connect Wallet
              </button>
            )}
          </div>
        </div>

        {error ? <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div> : null}
        {notice ? <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">{notice}</div> : null}
      </div>

      {!actor ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 text-slate-700 shadow-sm">
          <div className="text-lg font-semibold text-slate-900">Please connect</div>
          <p className="mt-2 text-sm text-slate-600">
            This app is read-only. It reads projects & pools from the <b>{CONTRACT}</b> contract and calculates rewards client-side (no smart contract writes).
          </p>
          <div className="mt-3 text-xs text-slate-500">RPC: {normalizeRpcEndpoint(rpc)}</div>
        </div>
      ) : (
        <>
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
              <div>
                <div className="text-sm font-semibold text-slate-900">Project</div>
                <div className="text-xs text-slate-500">
                  Only projects where your account is listed in <b>allowedAccounts</b> (the contract account can view everything).
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => loadCoreState({ silent: false })}
                  className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm hover:bg-slate-50"
                  disabled={projectsLoading}
                >
                  {projectsLoading ? "Loading..." : "Refresh"}
                </button>
              </div>
            </div>

            {(allowedProjects || []).length === 0 ? (
              <div className="mt-4 text-sm text-slate-600">No projects found for this account.</div>
            ) : (
              <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
                <div className="md:col-span-2">
                  <div className="text-xs text-slate-500">Select project</div>
                  <select
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                    value={selectedProjectId}
                    onChange={(e) => setSelectedProjectId(e.target.value)}
                  >
                    <option value="">—</option>
                    {(allowedProjects || []).map((p) => (
                      <option key={String(p.projectId)} value={String(p.projectId)}>
                        {String(p.projectName || `Project ${p.projectId}`)} (ID: {String(p.projectId)})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-xs text-slate-500">Selection</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">
                    {selectedProject ? String(selectedProject.projectName || `Project ${selectedProject.projectId}`) : "—"}
                  </div>
                  <div className="mt-1 text-xs text-slate-600">
                    Project ID: <b>{selectedProject ? String(selectedProject.projectId) : "—"}</b> · Pools: <b>{poolsForProject.length}</b>
                  </div>
                  {selectedProject?.homepageUrl ? (
                    <a
                      className="mt-2 block text-xs text-slate-600 underline underline-offset-2"
                      href={String(selectedProject.homepageUrl)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {String(selectedProject.homepageUrl)}
                    </a>
                  ) : null}
                </div>
              </div>
            )}
          </div>

          {selectedProject ? (
            <>
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">Rewards per Claim</div>
                  </div>

                  <div className="flex flex-col gap-2 md:flex-row md:items-end">
                  <div className="flex flex-col gap-1">
                    <div className="text-xs text-slate-500">Total rewards (tokens)</div>
                    <input
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                      value={totalRewardsInput}
                      onChange={(e) => setTotalRewardsInput(e.target.value)}
                      inputMode="decimal"
                      placeholder="2,500,000"
                    />
                  </div>

                  <button
                    type="button"
                    onClick={computeSelectedRewardPools}
                    className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                    disabled={POOL_WEIGHTS.every((s) => {
                      const p = rewardInputs[s.id];
                      const h = p ? String(p.poolHash) : "";
                      return !h || calcByPool?.[h]?.loading;
                    })}
                  >
                    Calculate (Pools 1–4)
                  </button>
                </div>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                  {POOL_WEIGHTS.map((slot) => {
                    const pool = rewardInputs[slot.id];
                    const h = pool ? String(pool.poolHash) : "";
                    const entry = h ? calcByPool?.[h] : null;
                    const isCalculated = !!pool && !!entry?.data && !entry?.loading && !entry?.error;
                    const eligible = Number(rewardNumbers.N[slot.id] || 0);
                    const showReward = isCalculated && eligible > 0;
                    const rewardValue = formatFixedTrunc(rewardNumbers.rewards[slot.id], 4);
                    const rewardSub = `${formatFixedTrunc(factorNumbers[slot.id], 4)} × TokensPerBonusPoint`;

                    return (
                      <PoolCard
                        key={slot.id}
                        slotLabel={slot.label}
                        pools={poolsForProject}
                        selectedPoolHash={rewardPoolHashBySlot[slot.id]}
                        onPoolChange={(v) => setRewardPoolHashBySlot((s) => ({ ...s, [slot.id]: v }))}
                        factorValue={factorInputs[slot.id]}
                        onFactorChange={(v) => setFactorInputs((s) => ({ ...s, [slot.id]: v }))}
                        pool={pool}
                        calcEntry={entry}
                        eligible={eligible}
                        rewardValue={rewardValue}
                        rewardSub={rewardSub}
                        isCalculated={isCalculated}
                        showReward={showReward}
                      />
                    );
                  })}
                </div>

                <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                  <StatBox
                    label="TotalBonus"
                    value={formatFixedTrunc(rewardNumbers.totalBonus, 4)}
                    sub={`Supply: ${formatIntPlain(totalRewards)} tokens`}
                  />
                  <StatBox
                    label="TokensPerBonusPoint"
                    value={formatFixedTrunc(rewardNumbers.tokensPerBonusPoint, 4)}
                    sub={`${formatIntPlain(totalRewards)} / TotalBonus`}
                  />
                </div>
              </div>

              

            </>
          ) : (
            <div className="rounded-2xl border border-slate-200 bg-white p-6 text-slate-700 shadow-sm">
              Please select a project.
            </div>
          )}
        </>
      )}
    </div>
  );
}
